import { useCallback, useEffect, useRef, useState } from 'react';
import type { InspectionMediaData, InspectionMediaType } from '../features/inspections/inspectionMediaTypes';

interface BaseModalState {
  isOpen: boolean;
  isResponsive: boolean;
  title: string;
  sessionKey: number;
}

export interface ImageModalState extends BaseModalState {
  imageData?: InspectionMediaData;
  type: InspectionMediaType;
}

interface UseImageModalReturn {
  modalState: ImageModalState;
  openModal: (imageData?: InspectionMediaData, type?: InspectionMediaType) => void;
  closeModal: () => void;
  setModalState: React.Dispatch<React.SetStateAction<ImageModalState>>;
}

const resolveMediaType = (imageData?: InspectionMediaData, type: InspectionMediaType = 'image'): InspectionMediaType => {
  const rawType = imageData?.raw && typeof imageData.raw === 'object' && 'type' in imageData.raw ? imageData.raw.type : undefined;
  return rawType === 'video' || rawType === 'image' ? rawType : type;
};

const useImageModal = (initialState?: Partial<ImageModalState>): UseImageModalReturn => {
  const [modalState, setModalState] = useState<ImageModalState>({
    isOpen: false,
    isResponsive: false,
    title: '',
    sessionKey: 0,
    type: 'image',
    imageData: undefined,
    ...initialState,
  });
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openSequenceRef = useRef(0);

  const clearPendingClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPendingClose(), [clearPendingClose]);

  /**
   * Open modal safely
   * Always sets fresh media state
   */
  const openModal = useCallback((imageData?: InspectionMediaData, type: InspectionMediaType = 'image') => {
    clearPendingClose();
    openSequenceRef.current += 1;

    setModalState({
      isOpen: true,
      isResponsive: true,
      title: imageData?.title ? `${imageData.title} View` : 'Preview',
      sessionKey: openSequenceRef.current,
      imageData: imageData ? { ...imageData } : undefined,
      type: resolveMediaType(imageData, type),
    });
  }, [clearPendingClose]);

  /**
   * Close modal safely
   * Prevents video unmount race condition
   */
  const closeModal = useCallback(() => {
    clearPendingClose();

    // 1. Close modal immediately (UI closes fast)
    setModalState(prev => ({
      ...prev,
      isOpen: false,
    }));

    // 2. Clear media AFTER close animation
    // Prevents: play() interrupted / media removed errors
    closeTimeoutRef.current = setTimeout(() => {
      setModalState(prev => ({
        ...(prev.isOpen
          ? prev
          : {
              ...prev,
              imageData: undefined,
              title: '',
              type: 'image',
            }),
      }));
      closeTimeoutRef.current = null;
    }, 150); // Safe delay (120–200ms works well)
  }, [clearPendingClose]);

  return {
    modalState,
    openModal,
    closeModal,
    setModalState,
  };
};

export default useImageModal;
